import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import styled from '@mui/styles/styled';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import GetApp from '@mui/icons-material/GetApp';
import Button from '@mui/material/Button';
import { FolderOutlined, Person, PlayCircleFilled } from '@mui/icons-material';
import Info from '@mui/icons-material/Info';
import { config } from '../../config';
import { useDownload } from '../../hooks/useDownload';
import { ProductSummaryFetcher } from '../../fetchers/ProductSummaryFetcher';
import { useTranslation } from 'react-i18next';

const StyledPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  padding: `${theme.spacing(8)} 5.5%`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

const StepIndex = styled('div')(({ theme }) => ({
  width: '1.5em',
  height: '1.5em',
  borderRadius: '0.75em',
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const StepContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: `${theme.spacing(2)}`,
}));

interface IStepProps {
  icon: FC;
  step: number | JSX.Element;
  title: string;
  description: string;
}

const Step: FC<IStepProps> = ({ icon, step, title, description, children }) => {
  const StyledIcon = useMemo(() => {
    return styled(icon)({
      width: '48px',
      height: '48px',
    });
  }, [icon]);

  return (
    <Grid item xs={6} lg={3}>
      <StepContainer>
        <StyledIcon />
        {typeof step === 'number' ? <StepIndex>{step}</StepIndex> : step}
        <Typography variant={'h6'}>{title}</Typography>
        <Typography variant={'body2'} align="center">
          {description}
        </Typography>
        {children}
      </StepContainer>
    </Grid>
  );
};

export const DiscoveryOverview: FC = () => {
  const { t } = useTranslation();
  const { data } = useContext(ProductSummaryFetcher.Context);

  const download = useDownload();
  const [isPending, setPending] = useState(false);

  const onDownload = useCallback(async () => {
    const installers = data ? [...data.installer] : [];

    installers.sort((a, b) => b.released - a.released);

    setPending(true);
    await download(installers[0].filePath);
    setPending(false);
  }, [download, data]);

  return (
    <Grid item xs={12} lg={8}>
      <StyledPaper>
        <Grid container spacing={4}>
          <Step
            icon={GetApp}
            step={1}
            title={t('overview.discovery.step1.title')}
            description={t('overview.discovery.step1.description')}
          >
            {(data?.installer?.length ?? 0) > 0 ? (
              <Button color="primary" variant="contained" onClick={onDownload} disabled={isPending}>
                {t('overview.discovery.step1.button')}
              </Button>
            ) : null}
          </Step>

          <Step
            icon={FolderOutlined}
            step={2}
            title={t('overview.discovery.step2.title')}
            description={t('overview.discovery.step2.description')}
          />

          <Step
            icon={PlayCircleFilled}
            step={3}
            title={t('overview.discovery.step3.title')}
            description={t('overview.discovery.step3.description')}
          />

          <Step
            icon={Person}
            step={<Info viewBox={'2 2 20 20'} />}
            title={t('overview.discovery.step4.title')}
            description={t('overview.discovery.step4.description')}
          >
            <Button variant="outlined" color="primary" href={`mailto:${config.salesEmail}`}>
              {t('overview.discovery.step4.button')}
            </Button>
          </Step>
        </Grid>
      </StyledPaper>
    </Grid>
  );
};
